import { Button } from '@cube3/cubicle/src/core/atoms/Button/Button';
import InputField from '@cube3/cubicle/src/core/atoms/InputField/InputField';
import { HBox, Free } from '@cube3/cubicle/src/core/templates/layout/Flex';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useGenerativeContext } from '../contexts/GenerativeContext';
import { useGenericFormState } from '@cube3/forms/src/hooks/useGenericFormState';

import { useSelection } from '@cube3/state/src/redux/components/useSelection';
import { useUploads } from '@cube3/state/src/redux/components/Hooks/useUploads';
import {
  ASPECT_RATIOS,
  Generation
} from '@cube3/common/model/schema/resources/generation';
import { useFetchImageSrc } from '../hooks/useFetchImageSrc';
import { Field, Value } from '@cube3/forms/src/types/GenericFormTypes';
import { objectFromValues } from '@cube3/forms/src/utils/objectToFormValues';
import { useLocalImages } from '../hooks/useLocalImages';
import { GenerationSettings } from './GenerationSettings';
import {
  GenerationConfig,
  GenerationSettingsType
} from '../hooks/useGenerateImage';
import { GenerationHistory } from './GenerationHistory';

type PromptInputConfig = {
  generation: Generation;
  onGenerate: (config: GenerationConfig) => void;
  loading: boolean;
  error: string;
};

const settingsFields = [
  {
    id: 'reference_enabled',
    type: 'checkbox',
    text: 'Use current image as input', // use the current generated image as reference
    label: 'Reference'
  },
  {
    id: 'aspect_ratio',
    type: 'select',
    label: 'Aspect Ratio',
    options: ASPECT_RATIOS,
    showErrors: false,
    width: 32,
    showClearButton: false
  },
  {
    id: 'seed',
    type: 'string',
    placeholder: 'Random',
    label: 'Seed',
    width: 32
  }
] as Field[];

const initialSettings = [
  { id: 'reference_enabled', value: true },
  { id: 'aspect_ratio', value: '16x9' },
  { id: 'seed', value: '' }
] as Value[];

const emptyArray = [];

export const PromptInput = ({
  generation,
  onGenerate,
  loading: submiting,
  error
}: PromptInputConfig) => {
  const [prompt, setPrompt] = useState('');
  const [file, setFile] = useState<{ file: File; url: string }>(null);
  const [saveImageStatus, setSaveImageStatus] = useState<
    'Saving' | 'Saved' | 'Save failed'
  >(undefined);

  const { fields, values } = useGenericFormState({
    fields: settingsFields,
    initialValues: initialSettings
  });

  const { reference_enabled, ...config } = objectFromValues(values);
  const {
    selections,
    activeGeneration,
    prefilledPrompt,
    goBack,
    saveGeneration,
    updateGeneration
  } = useGenerativeContext();

  const [initialBase64, setInitialBase64] = useState(activeGeneration?.base64);
  const showBase64 = useRef(true);
  const onChange = useCallback(() => {
    setInitialBase64(null);
    showBase64.current = true;
  }, []);
  const { images, ...restConfig } = useLocalImages(1, 0.85, onChange);
  /** The selected image from local directory, currently only 1 image can be selected  */
  const base64 = initialBase64 || images[0];

  const base64ImagesUI = useMemo(
    () => (initialBase64 ? [initialBase64] : images),
    [initialBase64, images]
  );

  const hasBase64Image = base64 && activeGeneration?.base64?.src !== base64.src;

  /** The base64 is the last one it will try to use, so we should NOT send `reference_generation` when
   * - it is the first time to use the base64 image,
   * - or the base64 has changed
   */
  const referenceGenerationId =
    !reference_enabled || hasBase64Image ? undefined : activeGeneration?.id;

  const handleSubmit = useCallback(() => {
    // create `generation` resource
    onGenerate({
      settings: { ...(config as GenerationSettingsType), prompt },
      referenceAssets: selections,
      referenceGeneration: referenceGenerationId,
      referenceBase64: base64?.src
    });
  }, [prompt, config, selections, base64, referenceGenerationId, onGenerate]);

  // upload the generated image to the current location
  const { visiting } = useSelection();
  const cb = useCallback((f, u) => setFile({ file: f, url: u }), []);
  const { fetchImage } = useFetchImageSrc(cb, true);

  const { uploadFilesToFolder, uploadsForFolder } = useUploads({
    parentFolderId: visiting?.id
  });

  const handleSaveImage = useCallback(() => {
    setSaveImageStatus('Saving');
  }, []);

  // save generation to context
  useEffect(() => {
    if (generation?.url) {
      saveGeneration({ ...generation, base64: base64 });
      // fetch the image src and cache it before the url expires
      fetchImage(generation.url, generation.prompt);
      // reset the input
      setPrompt('');
      // hide the selected local images after submit
      showBase64.current = false;
      setInitialBase64(base64);
    }
  }, [generation]);

  useEffect(() => {
    if (file) {
      // overwrite the url to a blob url so that even if the original image url expires, we can still view/save the image
      updateGeneration(activeGeneration, file);
    }
  }, [file]);

  // upload the image
  const cachedImage = activeGeneration?.file;
  useEffect(() => {
    if (saveImageStatus === 'Saving' && cachedImage) {
      uploadFilesToFolder([cachedImage]);
    }
  }, [saveImageStatus, cachedImage]);

  const id = activeGeneration?.id;
  useEffect(() => {
    setPrompt(prefilledPrompt);
    // Reset the image save status when the image changes
    setSaveImageStatus(undefined);
    setFile(null);
    setInitialBase64(activeGeneration?.base64);
    // add the base64 image back when use the history navigation to go back
    if (initialBase64 && initialBase64.src !== activeGeneration?.base64?.src) {
      showBase64.current = true;
    }
  }, [id]);

  const upload =
    cachedImage &&
    uploadsForFolder?.length &&
    uploadsForFolder.find((res) => res.file === cachedImage);

  useEffect(() => {
    if (upload) {
      setSaveImageStatus(
        upload.failed
          ? 'Save failed'
          : upload.progress === 100
          ? 'Saved'
          : 'Saving'
      );
    }
  }, [upload]);

  const submitDisabled =
    !prompt ||
    !prompt.trim() ||
    submiting ||
    prompt === activeGeneration?.prompt;

  useEffect(() => {
    if (upload && upload.progress === 100) {
      updateGeneration(activeGeneration, { saved: true });
    }
  }, [upload]);

  return (
    <HBox align="center" gap={16} style={{ padding: '8px 16px' }}>
      <Button
        iconLeft="chevron_left"
        background="secondary"
        buttonStyle="outline"
        onClick={goBack}
      />
      <GenerationHistory />
      <Free>
        <InputField
          placeholder="Enter a text prompt..."
          value={prompt}
          onChange={setPrompt}
          onSubmit={handleSubmit}
          fullwidth={true}
          disabled={submiting}
          iconRight={
            <GenerationSettings
              fields={fields}
              error={error}
              submitDisabled={submitDisabled}
              handleSubmit={handleSubmit}
              {...restConfig}
              images={showBase64.current ? base64ImagesUI : emptyArray}
            />
          }
        />
      </Free>

      <Button
        label={saveImageStatus || 'Save Image'}
        disabled={
          submiting ||
          !generation?.url ||
          !!saveImageStatus ||
          !activeGeneration || // the shown images are the selection
          activeGeneration.saved
        }
        onClick={handleSaveImage}
        background="primary"
        buttonStyle="solid"
      />
    </HBox>
  );
};
